import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormHelperText,
  Divider,
  Alert
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';

/**
 * BogoAttendeeFields Component
 * Reusable component for collecting BOGO attendee information
 * Used in BankTransferStep and ETransferStep
 *
 * @param {Object} bogoData - The BOGO form data
 * @param {Function} onUpdateBogoData - Callback to update BOGO data
 * @param {Object} errors - Validation errors
 * @param {string} formattedSurcharge - Formatted surcharge amount with tax (e.g., "$565.00 CAD")
 */
const BogoAttendeeFields = ({
  bogoData,
  onUpdateBogoData,
  errors = {},
  formattedSurcharge = '$500.00'
}) => {
  const handleChange = (field) => (e) => {
    onUpdateBogoData({ [field]: e.target.value });
  };

  const handleAccommodationChange = (e) => {
    onUpdateBogoData({ accommodationPreference: e.target.value });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        mt: 3,
        bgcolor: 'primary.50',
        borderColor: 'primary.200',
        borderWidth: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" color="primary.main">
          BOGO Attendee Information
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please provide the names for both attendees who will be joining the trip.
      </Typography>

      {/* Attendee 1 */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Attendee 1
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            name="attendee1FirstName"
            label="First Name"
            value={bogoData.attendee1FirstName}
            onChange={handleChange('attendee1FirstName')}
            fullWidth
            required
            error={!!errors.attendee1FirstName}
            helperText={errors.attendee1FirstName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="attendee1LastName"
            label="Last Name"
            value={bogoData.attendee1LastName}
            onChange={handleChange('attendee1LastName')}
            fullWidth
            required
            error={!!errors.attendee1LastName}
            helperText={errors.attendee1LastName}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Attendee 2 */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Attendee 2
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            name="attendee2FirstName"
            label="First Name"
            value={bogoData.attendee2FirstName}
            onChange={handleChange('attendee2FirstName')}
            fullWidth
            required
            error={!!errors.attendee2FirstName}
            helperText={errors.attendee2FirstName}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name="attendee2LastName"
            label="Last Name"
            value={bogoData.attendee2LastName}
            onChange={handleChange('attendee2LastName')}
            fullWidth
            required
            error={!!errors.attendee2LastName}
            helperText={errors.attendee2LastName}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Accommodation Preference */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <HotelIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle1" fontWeight="bold">
          Accommodation Preference
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This helps us plan accommodation arrangements for the trip.
      </Typography>

      <FormControl component="fieldset" error={!!errors.accommodationPreference}>
        <RadioGroup
          value={bogoData.accommodationPreference}
          onChange={handleAccommodationChange}
        >
          <FormControlLabel
            value="same_room"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">
                  Same Room (1 Bed Shared)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Both attendees share one bed
                </Typography>
              </Box>
            }
            sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
          />
          <FormControlLabel
            value="shared_two_beds"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">
                  Shared Room (2 Beds)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Both attendees in same room with separate beds
                </Typography>
              </Box>
            }
            sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
          />
          <FormControlLabel
            value="separate_rooms"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">
                  Separate Rooms
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Each attendee gets their own private room
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
          />
        </RadioGroup>
        {errors.accommodationPreference && (
          <FormHelperText>{errors.accommodationPreference}</FormHelperText>
        )}
      </FormControl>

      {/* Separate Rooms Surcharge Notice */}
      <Alert
        severity="info"
        sx={{ mt: 2 }}
        icon={<HotelIcon />}
      >
        <Typography variant="body2">
          <strong>Note:</strong> Selecting "Separate Rooms" will add an additional{' '}
          <strong>{formattedSurcharge}</strong> (including tax) to your total.
        </Typography>
      </Alert>
    </Paper>
  );
};

export default BogoAttendeeFields;
